import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Wifi,
  WifiOff,
  RefreshCw,
  Trash2,
  Activity,
  Clock,
  HardDrive,
  Loader2,
  AlertTriangle,
} from 'lucide-react';
import { useOffline, formatBytes, formatRelativeTime } from '@/hooks/useOffline';
import { usePermissions } from '@/hooks/usePermissions';
import { cn } from '@/lib/utils';

interface StatusDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function StatusDialog({ open, onOpenChange }: StatusDialogProps) {
  const {
    isOnline,
    isSyncing,
    pendingCount,
    failedCount,
    lastSyncAt,
    syncNow,
    retryFailed,
    discardFailed,
    testConnection,
    resetLocalData,
    getStorageUsage,
  } = useOffline();

  const { hasPermission } = usePermissions();
  const isAdmin = hasPermission('settings.manage');

  const [connectionTest, setConnectionTest] = useState<{
    testing: boolean;
    result: { success: boolean; latency: number } | null;
  }>({ testing: false, result: null });

  const [storageUsage, setStorageUsage] = useState<{ used: number; quota: number } | null>(null);
  const [isResetting, setIsResetting] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  useEffect(() => {
    if (open) {
      getStorageUsage().then(setStorageUsage);
    }
  }, [open, getStorageUsage]);

  const handleTestConnection = async () => {
    setConnectionTest({ testing: true, result: null });
    const result = await testConnection();
    setConnectionTest({ testing: false, result });
  };

  const handleResetData = async () => {
    setIsResetting(true);
    try {
      await resetLocalData();
      setShowResetConfirm(false);
    } finally {
      setIsResetting(false);
    }
  };

  const handleSync = async () => {
    await syncNow();
  };

  const [isRetryingFailed, setIsRetryingFailed] = useState(false);
  const [isDiscardingFailed, setIsDiscardingFailed] = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);

  const handleRetryFailed = async () => {
    setIsRetryingFailed(true);
    try {
      await retryFailed();
    } finally {
      setIsRetryingFailed(false);
    }
  };

  const handleDiscardFailed = async () => {
    setIsDiscardingFailed(true);
    try {
      await discardFailed();
      setShowDiscardConfirm(false);
    } finally {
      setIsDiscardingFailed(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Status</DialogTitle>
            <DialogDescription>
              View sync status and manage local data cache.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Connection Status */}
            <div className="flex items-center justify-between p-3 rounded-lg border bg-card">
              <div className="flex items-center gap-3">
                {isOnline ? (
                  <div className="p-2 rounded-full bg-green-500/10">
                    <Wifi className="h-4 w-4 text-green-500" />
                  </div>
                ) : (
                  <div className="p-2 rounded-full bg-red-500/10">
                    <WifiOff className="h-4 w-4 text-red-500" />
                  </div>
                )}
                <div>
                  <p className="font-medium">Connection</p>
                  <p className="text-sm text-muted-foreground">
                    {isOnline ? 'Online' : 'Offline'}
                  </p>
                </div>
              </div>
              <Badge variant={isOnline ? 'default' : 'destructive'}>
                {isOnline ? 'Connected' : 'Disconnected'}
              </Badge>
            </div>

            {/* Sync Status */}
            <div className="flex items-center justify-between p-3 rounded-lg border bg-card">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-primary/10">
                  <RefreshCw className={cn('h-4 w-4 text-primary', isSyncing && 'animate-spin')} />
                </div>
                <div>
                  <p className="font-medium">Sync Status</p>
                  <p className="text-sm text-muted-foreground">
                    {isSyncing
                      ? 'Syncing...'
                      : pendingCount > 0
                      ? `${pendingCount} pending`
                      : 'Up to date'}
                  </p>
                </div>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={handleSync}
                disabled={!isOnline || isSyncing}
              >
                {isSyncing ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  'Sync Now'
                )}
              </Button>
            </div>

            {/* Failed items (dead-lettered after exhausting retries) */}
            {failedCount > 0 && (
              <div className="flex items-center justify-between gap-3 p-3 rounded-lg border border-destructive/30 bg-destructive/5">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="p-2 rounded-full bg-destructive/10 shrink-0">
                    <AlertTriangle className="h-4 w-4 text-destructive" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium">Failed to Sync</p>
                    <p className="text-sm text-muted-foreground">
                      {failedCount} item{failedCount === 1 ? '' : 's'} couldn't be synced
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleRetryFailed}
                    disabled={!isOnline || isRetryingFailed || isSyncing}
                  >
                    {isRetryingFailed ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Retry'}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-destructive hover:text-destructive"
                    onClick={() => setShowDiscardConfirm(true)}
                    disabled={isDiscardingFailed}
                  >
                    Discard
                  </Button>
                </div>
              </div>
            )}

            {/* Last Sync */}
            <div className="flex items-center justify-between p-3 rounded-lg border bg-card">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-muted">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                </div>
                <div>
                  <p className="font-medium">Last Synced</p>
                  <p className="text-sm text-muted-foreground">
                    {formatRelativeTime(lastSyncAt)}
                  </p>
                </div>
              </div>
            </div>

            {/* Storage Usage */}
            {storageUsage && (
              <div className="flex items-center justify-between p-3 rounded-lg border bg-card">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-full bg-muted">
                    <HardDrive className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <div>
                    <p className="font-medium">Local Storage</p>
                    <p className="text-sm text-muted-foreground">
                      {formatBytes(storageUsage.used)} used
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Connection Test (Admin only) */}
            {isAdmin && (
              <div className="flex items-center justify-between p-3 rounded-lg border bg-card">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-full bg-muted">
                    <Activity className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <div>
                    <p className="font-medium">Connection Test</p>
                    <p className="text-sm text-muted-foreground">
                      {connectionTest.result
                        ? connectionTest.result.success
                          ? `${connectionTest.result.latency}ms latency`
                          : 'Connection failed'
                        : 'Test server latency'}
                    </p>
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleTestConnection}
                  disabled={connectionTest.testing}
                >
                  {connectionTest.testing ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    'Test'
                  )}
                </Button>
              </div>
            )}

            {/* Reset Local Data */}
            <div className="pt-2 border-t">
              <Button
                variant="destructive"
                className="w-full"
                onClick={() => setShowResetConfirm(true)}
                disabled={isResetting}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Reset Local Data
              </Button>
              <p className="mt-2 text-xs text-muted-foreground text-center">
                Clears cached data and re-downloads from server. Use if data appears out of sync.
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Discard Failed Confirmation Dialog */}
      <AlertDialog open={showDiscardConfirm} onOpenChange={setShowDiscardConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard failed items?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes {failedCount} item{failedCount === 1 ? '' : 's'} that
              couldn't be synced to the server. The change{failedCount === 1 ? '' : 's'} will be
              lost. Use "Retry" instead if the connection or data issue has been resolved.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDiscardingFailed}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDiscardFailed}
              disabled={isDiscardingFailed}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDiscardingFailed ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Discarding...
                </>
              ) : (
                'Discard'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reset Confirmation Dialog */}
      <AlertDialog open={showResetConfirm} onOpenChange={setShowResetConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset Local Data?</AlertDialogTitle>
            <AlertDialogDescription>
              This will clear all cached data stored in your browser and re-download fresh data from
              the server. Any unsynced changes will be lost.
              {pendingCount > 0 && (
                <span className="block mt-2 font-medium text-destructive">
                  Warning: You have {pendingCount} pending item(s) that have not been synced.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isResetting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleResetData}
              disabled={isResetting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isResetting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Resetting...
                </>
              ) : (
                'Reset Data'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
